import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ThemeMode } from "../types";
import { menuIcon, menuRow } from "./menuStyles";
import { trackThemeSubmenuToggle, trackThemeChange } from "../sidebarAnalytics";

interface ThemeSubmenuProps {
  currentTheme: ThemeMode;
  onThemeChange: (theme: ThemeMode) => void;
  isDarkMode: boolean;
}

interface ThemeOption {
  value: ThemeMode;
  labelKey: string;
  icon: string;
}

const THEME_OPTIONS: ThemeOption[] = [
  { value: "light", labelKey: "theme.light", icon: "ri-sun-line" },
  { value: "dark", labelKey: "theme.dark", icon: "ri-moon-line" },
  { value: "system", labelKey: "theme.system", icon: "ri-computer-line" },
];

export function ThemeSubmenu({ currentTheme, onThemeChange, isDarkMode }: ThemeSubmenuProps) {
  const { t } = useTranslation("common");
  const [isOpen, setIsOpen] = useState(false);

  const currentOption = THEME_OPTIONS.find((opt) => opt.value === currentTheme) || THEME_OPTIONS[2];

  const handleToggle = () => {
    const nextState = !isOpen;
    setIsOpen(nextState);
    trackThemeSubmenuToggle(nextState ? "open" : "close");
  };

  const handleSelect = (theme: ThemeMode) => {
    trackThemeChange(currentTheme, theme);
    onThemeChange(theme);
    setIsOpen(false);
  };

  return (
    <div>
      <button type="button" onClick={handleToggle} aria-expanded={isOpen} className={menuRow(isDarkMode)}>
        <i className={`${currentOption.icon} ${menuIcon(isDarkMode)}`} aria-hidden />
        <span>{t("theme.label")}</span>
        <span className={`ml-auto text-[13px] ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>{t(currentOption.labelKey)}</span>
        <i
          className={`ri-arrow-right-s-line text-base transition-transform duration-200 ${isOpen ? "rotate-90" : ""} ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
          aria-hidden
        />
      </button>

      {isOpen && (
        <div role="radiogroup" aria-label={t("theme.label")} className="pl-6">
          {THEME_OPTIONS.map((option) => (
            <ThemeOptionButton
              key={option.value}
              option={option}
              isSelected={currentTheme === option.value}
              isDarkMode={isDarkMode}
              onSelect={handleSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface ThemeOptionButtonProps {
  option: ThemeOption;
  isSelected: boolean;
  isDarkMode: boolean;
  onSelect: (theme: ThemeMode) => void;
}

function ThemeOptionButton({ option, isSelected, isDarkMode, onSelect }: ThemeOptionButtonProps) {
  const { t } = useTranslation("common");
  return (
    <button type="button" role="radio" aria-checked={isSelected} onClick={() => onSelect(option.value)} className={menuRow(isDarkMode)}>
      <i className={`${option.icon} ${menuIcon(isDarkMode)}`} aria-hidden />
      <span className="flex-1">{t(option.labelKey)}</span>
      {isSelected && <i className="ri-check-line text-base" aria-hidden />}
    </button>
  );
}
